import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonConnectModal } from '@tonconnect/ui-react';

import {
  useBuyPaidLineMutation,
  useLazyGetRewardsQuery,
} from '@/api/queries/rewards';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { useBuyPaidPlanRequest } from '@/common/hooks/buyPaidPlanRequest';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { multiplication } from '@/common/lib/decimal';
import { SendAnalitics } from '@/common/lib/playdeck';
import {
  H1,
  H2,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import { getPaidRewardPlanCost } from '@/redux/selectors/earnRewardsSelector';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import paidPlanRewardPng from '/img/rewardsModal/paid-plan-reward.png';
import paidPlanRewardWebp from '/img/rewardsModal/paid-plan-reward.webp';
import coinPng from '/img/tgem.png';
import coinWebp from '/img/tgem.webp';

const BuyPaidLineModal: React.FC = () => {
  const { t } = useTranslation();
  const { closeModal, secondModal, thirdModal } = useContext(ModalContext);
  const paymentStatus = usePaymentStatus();
  const dispatch = useAppDispatch();
  const [buyPaidPlanMutation] = useBuyPaidLineMutation();
  const [trigger] = useLazyGetRewardsQuery();

  const [waiting, setWaiting] = useState<boolean>(false);
  const cost = useAppSelector(getPaidRewardPlanCost);
  const usdPrice = useMemo(
    () =>
      multiplication(
        cost?.amountUsd ?? 0,
        additionalDiscountForUsdt.coefficient,
      ),
    [cost],
  );

  usePurchaseAttempt({
    item: 'daily plan',
    item_id: 'daily plan',
    price: cost?.amountUsd ?? 0,
    currency: 'usdt',
  });

  const buyPaidPlan = useBuyPaidPlanRequest();
  const { state } = useTonConnectModal();

  const sendAnalitics = (currency: 'ton' | 'xtr' | 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: 'daily plan',
      item_id: 'daily plan',
      price: cost?.amountUsd ?? 0,
      currency,
    });
  };

  const onClick = (currency: 'XTR' | 'TON'): void => {
    setWaiting(true);
    buyPaidPlan(currency)
      .then(() => {
        dispatch(changePaymentStatus('pending'));
        sendAnalitics(currency.toLowerCase() as 'ton' | 'xtr');
        setWaiting(false);
      })
      .catch(() => {
        dispatch(changePaymentStatus(undefined));
        setWaiting(false);
      });
  };

  const buyUsdt = async (): Promise<void> => {
    try {
      await buyPaidPlanMutation({ currency: 'usdt' }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      dispatch(changePaymentStatus('success'));
      sendAnalitics('usdt');
      trigger();

      ToastNotification(t('Success. T-Gem credited and ready to use'), {
        img: coinPng,
        webp: coinWebp,
      });
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'), {
        img: coinPng,
        webp: coinWebp,
      });
      dispatch(changePaymentStatus(undefined));
    }
  };

  useEffect(() => {
    if (state.closeReason === 'action-cancelled') setWaiting(false);
  }, [state.closeReason]);

  const handleClose = (): void => {
    closeModal();
    secondModal.closeModal();
    dispatch(changePaymentStatus(undefined));
  };

  return (
    <div className="flex flex-col items-center pt-[var(--header-height)]">
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : paymentStatus === 'success' ? (
        <SuccessfullStatus>
          <div className="w-40 mt-4">
            <Picture
              alt="paid plan reward"
              webp={paidPlanRewardWebp}
              img={paidPlanRewardPng}
            />
          </div>
        </SuccessfullStatus>
      ) : (
        <>
          <div className="gap-4 items-center self-stretch flex px-4">
            <span className="flex-1 h-px rounded-md from-white/0 to-white bg-gradient-to-r" />
            <H1>{t('Purchase')}</H1>
            <span className="flex-1 h-px rounded-md from-white/0 to-white bg-gradient-to-l" />
          </div>

          <div className="text-center mt-[6.25rem]">
            <H2 weight="bold">{t('Epic Reward Track')}</H2>
            <StringSmall className="text-white/60 mt-1">
              {t('Start your 20-day reward journey!')}
            </StringSmall>
          </div>
          <div className="mt-[0.875rem] flex items-center justify-center relative size-40">
            <Picture
              alt="paid plan reward"
              webp={paidPlanRewardWebp}
              img={paidPlanRewardPng}
              className="w-full h-max absolute top-0 z-10"
            />
            <div className="absolute bg-[#858EA9] rounded-full blur-[100px] w-[14.25rem] h-[8.5rem]" />
          </div>
          <StringMain weight="black" className="!text-[40px] mt-5">
            $ {cost?.amountUsd}
          </StringMain>
          <div className="px-6 mt-10 self-stretch">
            <TONandStarsBuyButtons
              onClick={onClick}
              isLoading={waiting}
              price={cost?.amountUsd ?? 0}
              usdPrice={usdPrice}
              discount={additionalDiscountForUsdt.percentage}
              starPrice={cost?.amountStars ?? 0}
              mustBeUsdtButton={true}
              onUsdtBuy={buyUsdt}
              variant="switch"
              classNames="!px-0"
            />
            <GreyBlurButton
              className="mt-3"
              text={t('Close')}
              onClick={handleClose}
            />
          </div>
        </>
      )}
      {paymentStatus === 'pending' && (
        <Close
          onClick={handleClose}
          imageVariant="modal"
          size="lg"
          className="absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+30px)]"
        />
      )}
    </div>
  );
};

export default BuyPaidLineModal;
